type DocPage = {
  title: string;
  intro: string;
  sections: Array<[string, string[]]>;
};

export const marketplaceUrl = 'https://marketplace.visualstudio.com/items?itemName=AmeriuseSmartCAN.smartcan-vscode-extension';
export const extensionId = 'AmeriuseSmartCAN.smartcan-vscode-extension';
export const pythonSdkUrl = 'https://github.com/ameriuse/smartcan-python-sdk';
export const socialLinks = [
  {
    id: 'linkedin',
    label: 'LinkedIn',
    icon: 'linkedin',
    url: 'https://www.linkedin.com/company/110429149/',
    ariaLabel: 'Ameriuse on LinkedIn'
  },
  {
    id: 'instagram',
    label: 'Instagram',
    icon: 'instagram',
    url: 'https://www.instagram.com/ameriuse',
    ariaLabel: 'Ameriuse on Instagram'
  },
  {
    id: 'facebook',
    label: 'Facebook',
    icon: 'facebook',
    url: '',
    ariaLabel: 'Ameriuse on Facebook'
  },
  {
    id: 'github',
    label: 'GitHub',
    icon: 'github',
    url: pythonSdkUrl,
    ariaLabel: 'SmartCAN Python SDK on GitHub'
  },
  {
    id: 'marketplace',
    label: 'VS Code Marketplace',
    icon: 'marketplace',
    url: marketplaceUrl,
    ariaLabel: 'SmartCAN VS Code Extension on Visual Studio Marketplace'
  }
];
export const screenshotUrls = [
  {
    title: 'USB High-Rate Live Monitoring',
    src: 'https://raw.githubusercontent.com/ameriuse/smartcan-pro/main/screenshots/usb-high-rate.png',
    alt: 'SmartCAN Pro USB High-Rate Live Monitoring screenshot showing USB High Rate, many CAN rows, and USB power'
  },
  {
    title: 'Wi-Fi Snapshot Mode',
    src: 'https://raw.githubusercontent.com/ameriuse/smartcan-pro/main/screenshots/wifi-snapshot.png',
    alt: 'SmartCAN Pro Wi-Fi Snapshot Mode screenshot showing WiFi transport, fewer CAN rows, and battery or power percent'
  },
  {
    title: 'CAN ID Filtering and Byte Highlighting',
    src: 'https://raw.githubusercontent.com/ameriuse/smartcan-pro/main/screenshots/filter-highlight.png',
    alt: 'SmartCAN Pro CAN ID Filtering and Byte Highlighting screenshot showing filter input 0x025 and one matching CAN row'
  }
];

export const features = [
  ['USB High-Rate Live Monitoring', 'Raw binary streaming for professional real-time CAN capture and analysis.'],
  ['Wi-Fi Snapshot Dashboard', 'Browser access to current device state, lightweight live frames, files, and diagnostics.'],
  ['CANLOG01 File Viewer', 'Inspect captured CAN log files and prepare exports for offline workflows.'],
  ['Python SDK + CLI', 'Script device status, automation, capture checks, and data processing with smartcan tooling.'],
  ['VS Code Extension', 'Developer-first USB live view, file inspection, export tools, and signal discovery.'],
  ['SD Card Logging', 'Capture CAN traffic locally on the device for field and bench sessions.'],
  ['Signal Discovery', 'Find changing bytes and candidate signals during reverse engineering work.'],
  ['Export CSV / JSON', 'Move captures into notebooks, dashboards, data pipelines, and customer reports.'],
  ['Read-only CAN Monitoring', 'Default ListenOnly firmware posture for passive monitoring without bus control.']
];

export const useCases = [
  'Automotive diagnostics',
  'Embedded development',
  'Robotics CAN networks',
  'Industrial systems',
  'Reverse engineering',
  'Fleet / dealership workflows'
];

export const docs = {
  '/docs': {
    title: 'SmartCAN Pro Documentation',
    intro: 'Start here for setup, dashboard behavior, extension workflows, SDK usage, protocol notes, and troubleshooting.',
    sections: [
      ['Quick links', ['Quickstart', 'Developer Setup', 'Downloads', 'SmartCAN Web Analyzer', 'SmartCAN Web Live Mode', 'VS Code Extension', 'Python SDK', 'Device Protocol', 'Troubleshooting']],
      ['Product posture', ['SmartCAN Pro Dev Kit is in active development.', 'VS Code Extension and Python SDK are available for testing.', 'The web dashboard uses Wi-Fi Snapshot Mode for lightweight monitoring.']],
      ['Learn more', ['What is SmartCAN?: /what-is-smartcan', 'Live CAN bus monitor: /live', 'CAN log analyzer: /analyzer', 'Community CAN signals: /decode']]
    ]
  },
  '/docs/quickstart': {
    title: 'Quickstart',
    intro: 'Bring SmartCAN Pro online for passive CAN monitoring and logging.',
    sections: [
      ['Setup steps', ['Power SmartCAN Pro.', 'Connect USB or join the SmartCAN Wi-Fi network.', 'Open the VS Code Extension or the Web Dashboard.', 'Start logging when SD storage is ready.', 'Export data as CSV or JSON for analysis.']],
      ['Mode choice', ['Use USB for high-rate live capture.', 'Use Wi-Fi Snapshot Mode for status checks, files, diagnostics, and lightweight monitoring.']]
    ]
  },
  '/docs/developer-setup': {
    title: 'Developer Setup',
    intro: 'Set up the SmartCAN Pro developer workflow for extension, SDK, USB, Wi-Fi dashboard, and CANLOG01 exports.',
    sections: [
      ['Install VS Code Extension', [`Marketplace: ${marketplaceUrl}`, `VS Code Quick Open: ext install ${extensionId}`, `CLI: code --install-extension ${extensionId}`]],
      ['Clone GitHub repo', [`Python SDK: ${pythonSdkUrl}`, 'Open the repo in VS Code for SDK and automation workflows.']],
      ['Install Python SDK locally', ['git clone https://github.com/ameriuse/smartcan-python-sdk', 'cd smartcan-python-sdk', 'pip install -e .']],
      ['Connect SmartCAN over USB', ['Use USB High Rate mode for professional live monitoring.', 'Run SmartCAN: Connect from the Command Palette.']],
      ['Use web dashboard over Wi-Fi', ['Connect the computer or phone to the SmartCAN Wi-Fi network.', 'Enter the IP shown on your SmartCAN device, or try smartcan.local if mDNS is available on your network.']],
      ['Export CANLOG01 files', ['Use the VS Code Extension or dashboard files section to download and inspect CANLOG01 captures.']]
    ]
  },
  '/docs/analyzer': {
    title: 'SmartCAN Web Analyzer',
    intro: 'Inspect SmartCAN capture files directly in the browser without login, billing, or local installation.',
    sections: [
      ['Capabilities', ['Upload CANLOG01 files.', 'Inspect parsed CAN frames.', 'Filter CAN IDs and data bytes.', 'Export CSV or JSON.', 'No login required.']],
      ['Best fit', ['Use the Web Analyzer for offline capture review and quick sharing workflows.', 'For real-time live monitoring, use the VS Code extension and SmartCAN hardware.']]
    ]
  },
  '/docs/live': {
    title: 'SmartCAN Web Live Mode',
    intro: 'Connect to a SmartCAN device over Wi-Fi and capture live-polled CAN frames directly in the browser.',
    sections: [
      ['How it works', ['Enter the SmartCAN device IP or HTTP URL.', 'Connect to /api/status to confirm the device is reachable.', 'Start Live to poll /api/live and append frames to the in-browser capture buffer.', 'Pause, resume, stop, clear, filter, and export the captured frames.']],
      ['Decode integration', ['Select a Decode Cloud profile.', 'Enable Decode live frames to send small batches to /api/decode/frames.', 'Unknown frames remain visible in the raw frame table.', 'Frames stay in the browser unless decode is enabled.']],
      ['Compatibility notes', ['CORS is required for browser access to the SmartCAN device API.', 'HTTPS-to-HTTP local device fetches may be blocked by browser mixed-content protection even when the device API is reachable directly.', 'For real hardware, use the device local UI or the VS Code Extension until HTTPS/device-local hosting is available.', 'Demo Simulation Mode remains available without hardware.']],
      ['Demo Simulation Mode', ['Use Demo Simulation Mode to exercise the UI without SmartCAN hardware.', 'Synthetic CAN frames are generated locally and clearly labeled.']]
    ]
  },
  '/docs/downloads': {
    title: 'Downloads',
    intro: 'Current SmartCAN Pro downloads and install paths.',
    sections: [
      ['VS Code Extension', [`Marketplace: ${marketplaceUrl}`, `VS Code Quick Open: ext install ${extensionId}`, `CLI: code --install-extension ${extensionId}`]],
      ['SmartCAN Web Analyzer', ['Upload CANLOG01 files.', 'Inspect frames.', 'Filter CAN IDs.', 'Export CSV/JSON.', 'No login required.']],
      ['SmartCAN Web Live Mode', ['Connect to a SmartCAN device over Wi-Fi.', 'Poll /api/live.', 'Optionally decode live batches.', 'Export captured CSV/JSON.', 'No login required.']],
      ['Python SDK', [`GitHub: ${pythonSdkUrl}`, 'git clone https://github.com/ameriuse/smartcan-python-sdk', 'cd smartcan-python-sdk', 'pip install -e .', 'PyPI package coming soon']],
      ['Firmware', ['Coming soon. Firmware downloads will be added when available.']],
      ['Sample CANLOG01', ['TODO: add sample capture file for testing the extension and web documentation.']]
    ]
  },
  '/docs/extension': {
    title: 'VS Code Extension',
    intro: 'The extension is the preferred interface for professional high-rate USB live monitoring.',
    sections: [
      ['Capabilities', ['USB serial connection to SmartCAN Pro.', 'Live CAN table with frame rate and drop visibility.', 'CANLOG01 file inspection.', 'CSV and JSON export.', 'Signal discovery workflows.']],
      ['Install', [`Marketplace: ${marketplaceUrl}`, `VS Code Quick Open: ext install ${extensionId}`, `CLI: code --install-extension ${extensionId}`]]
    ]
  },
  '/docs/sdk': {
    title: 'Python SDK',
    intro: 'Automate SmartCAN workflows using Python. The SDK is public on GitHub and the PyPI package is coming soon.',
    sections: [
      ['GitHub', [pythonSdkUrl]],
      ['Install', ['git clone https://github.com/ameriuse/smartcan-python-sdk', 'cd smartcan-python-sdk', 'pip install -e .']],
      ['Package status', ['PyPI package coming soon']],
      ['Example', ['from smartcan import SmartCAN', 'dev = SmartCAN.auto()', 'print(dev.status())', 'for frame in dev.live(): print(frame)']],
      ['Notes', ['USB live streaming is the right path for high-rate work.', 'Wi-Fi APIs are intended for status and snapshot workflows.']]
    ]
  },
  '/docs/protocol': {
    title: 'Device Protocol',
    intro: 'SmartCAN Pro exposes simple HTTP endpoints for Wi-Fi dashboard use and USB streaming for high-rate work.',
    sections: [
      ['Wi-Fi endpoints', ['/api/status returns device status and diagnostics.', '/api/live returns live or snapshot CAN frames when supported.', '/api/files returns SD card capture files.', '/api/download retrieves a selected capture file.', '/api/storage returns storage capacity when supported.']],
      ['Transport model', ['USB High Rate uses raw binary live streaming.', 'Wi-Fi Snapshot Mode is optimized for dashboard checks and lightweight monitoring.']]
    ]
  },
  '/docs/troubleshooting': {
    title: 'Troubleshooting',
    intro: 'Common setup and capture issues for SmartCAN Pro.',
    sections: [
      ['Device not found', ['Confirm power and USB cable quality.', 'Close other serial tools that may be holding the port.', 'Enter the IP shown on your SmartCAN device, or try smartcan.local if mDNS is available on your network.']],
      ['USB serial busy', ['Close the VS Code serial monitor, terminal sessions, and other CAN tools.', 'Unplug and reconnect the device after closing the busy process.']],
      ['Wi-Fi not reachable', ['Join the SmartCAN Wi-Fi network.', 'Use the IP shown on the device status screen.', 'Corporate VPNs, HTTPS-to-local-HTTP restrictions, and browser CORS policy can block local device calls.']],
      ['smartcan.local not resolving', ['Use the IP shown on your SmartCAN device instead.', 'Install or enable mDNS support on the host network if required.']],
      ['No CAN IDs visible', ['Confirm CAN-H/CAN-L wiring and ground reference.', 'Check bus speed and termination.', 'Verify the target system is awake and transmitting.']],
      ['Drops greater than 0', ['Use USB High Rate mode for professional live capture.', 'Reduce Wi-Fi dashboard polling.', 'Check SD card quality and available storage.']],
      ['Battery / power issues', ['Use USB power for long bench sessions.', 'Check battery percentage and power_state in diagnostics.']],
      ['CAN init failed', ['Check transceiver wiring, SPI status, and controller health.', 'Power cycle the device after correcting wiring.']],
      ['Windows CH340 driver', ['Install the CH340 USB serial driver if the device port does not appear.']],
      ['macOS serial permission', ['Grant terminal or VS Code permission to access removable and serial devices.']],
      ['Linux udev rules', ['Add a udev rule for the USB serial adapter and reconnect the device.']]
    ]
  }
} satisfies Record<string, DocPage>;
